import { Backdrop, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import SocialLogin from '../../components/SocialLogin'
import { useFirebase } from '../../contexts/firebase'
import useConfiguration from '../../hooks/useConfiguration'
import useLogin from '../../hooks/useLogin'
import getQueryParams from '../../utils/getQueryParams'
import HomeSkeleton from './HomeSkeleton'
import { HeaderText } from './styles'

const Home = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const { configuration } = useConfiguration()
  const { signInWithGoogle, signInWithFacebook, logout } = useFirebase()
  const { error, sendLogin, loginCompleted, socialToken, isLoading } = useLogin()

  const { userId } = getQueryParams({ url: location.search })

  useEffect(() => {
    if (socialToken && configuration) {
      sendLogin({ type: 'social', socialToken, userId, middleware: configuration?.extensions.sendLoginMiddleware })
      logout()
    }
  }, [socialToken, configuration, userId])

  useEffect(() => {
    if (loginCompleted) {
      navigate(`${location.pathname}/redirect${location.search}`, { state: location.pathname })
    }
  }, [loginCompleted, location, navigate])

  useEffect(() => {
    if (error && error.status === 400) {
      navigate(`signIn${location.search}`, { state: location.pathname })
    }
  }, [error, navigate, location])

  if (!configuration) {
    return <HomeSkeleton />
  }

  return (
    <Stack direction="column" p={4} height="100%">
      <Box display="flex" justifyContent="center" marginBottom={3}>
        <Paper elevation={0} style={configuration.extensions.webviewCustomizations?.logo}>
          <img
            loading="lazy"
            width="100%"
            height="100%"
            alt={`${configuration.name} logo`}
            src={configuration.webview.theme.logo}
          />
        </Paper>
      </Box>

      <HeaderText
        gutterBottom
        variant="h5"
        dangerouslySetInnerHTML={{
          __html: t('home.welcome'),
        }}
        color={configuration.webview.theme.customerColor}
      />

      <Button
        fullWidth
        state="home"
        size="large"
        component={Link}
        variant="contained"
        to={`/${configuration.slug}/signIn${location.search}`}
        style={{
          ...configuration.extensions.webviewCustomizations?.primaryButton,
          padding: '1rem 0',
          boxShadow: 'none',
          marginBottom: '32px',
          font: `600 1rem/1rem "Montserrat", sans-serif`,
        }}
      >
        {t('button.enter')} {configuration.name}
      </Button>

      {error && (
        <Typography variant="subtitle2" color="red" textAlign="center">
          {t('home.socialLoginError')}
        </Typography>
      )}

      <SocialLogin googleSignIn={signInWithGoogle} facebookSignIn={signInWithFacebook} />

      <Backdrop
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress size={80} style={{ color: configuration.webview.theme.customerColor }} />
      </Backdrop>
    </Stack>
  )
}

export default Home
